import { Link } from 'react-router-dom';
import { Shield, Mail, Briefcase } from 'lucide-react';
import { useBrand } from '@/hooks/useBrand';

export function Footer() {
  const brand = useBrand();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 border-t border-gray-800 text-gray-400">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-8 sm:py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            
            {/* Brand Section */}
            <div className="space-y-3 sm:col-span-2 lg:col-span-1">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-primary-600 flex items-center justify-center rounded-lg">
                  <Briefcase className="w-4 h-4 text-white" />
                </div>
                <span className="ml-2 text-lg font-bold text-white">
                  Rapid<span className="text-primary-500">Fix</span>
                </span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
                Connecting homeowners with trusted local service professionals across Ireland.
              </p>
              <div className="flex items-center gap-2 text-xs text-emerald-400">
                <Shield className="w-3.5 h-3.5" />
                <span>GDPR Compliant</span>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-3">Platform</h3>
              <ul className="space-y-2">
                <FooterLink to="/search">Find Professionals</FooterLink>
                <FooterLink to="/signup?role=provider">Become a Provider</FooterLink>
                <FooterLink to="/providers">Browse Providers</FooterLink>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-3">Support</h3>
              <ul className="space-y-2">
                <FooterLink to="/help">Help Center</FooterLink>
                <li>
                  <a 
                    href={`mailto:${brand.email}`} 
                    className="text-sm hover:text-primary-400 transition-colors flex items-center gap-2"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-3">Legal</h3>
              <ul className="space-y-2">
                <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
                <FooterLink to="/terms">Terms of Service</FooterLink>
                <FooterLink to="/security">Security</FooterLink>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="py-6 border-t border-gray-800">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <span>© {currentYear} RapidFix</span>
              <span className="hidden sm:inline text-gray-700">•</span>
              <span className="text-xs">{brand.config.legal.address.city}, {brand.config.legal.address.country}</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-2 py-1 bg-gray-800 rounded text-gray-400">EU Based</span>
              <span className="px-2 py-1 bg-gray-800 rounded text-gray-400">SSL Secured</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ to, children }: { to: string, children: React.ReactNode }) {
  return (
    <li>
      <Link
        to={to}
        className="text-sm text-gray-400 hover:text-primary-400 transition-colors"
      >
        {children}
      </Link>
    </li>
  );
}